import * as types from "../actions/ActionTypes";
import isEqual from "lodash/isEqual";

const initialState = {
  loading: false,
  error: null,
  monthSummary: [],
  monthMeta: null,
  activeMonthKey: "",
  monthClientsByKey: {},
  activeClientIdByMonth: {},
  activeTabByClient: {},
  clientInvoiceById: {},
  tabNewByClient: {},
  dirtyTabsByClient: {},
  tabSavingByClient: {},
  validationErrorsByClientTab: {},
  sync: {
    status: "new",
    message: "",
    updatedAt: null,
  },
};

const setNested = (source, key, value) => ({
  ...(source || {}),
  [key]: value,
});

const InvoicesReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.INVOICES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.INVOICES_MONTH_SUMMARY_SUCCESS:
      return {
        ...state,
        loading: false,
        monthSummary: action.monthSummary || [],
        monthMeta: action.meta || null,
      };

    case types.INVOICES_SET_ACTIVE_MONTH:
      return {
        ...state,
        activeMonthKey: action.monthKey || "",
      };

    case types.INVOICES_MONTH_CLIENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        monthClientsByKey: setNested(
          state.monthClientsByKey,
          action.monthKey,
          action.clients || []
        ),
      };

    case types.INVOICES_SET_ACTIVE_CLIENT:
      return {
        ...state,
        activeClientIdByMonth: setNested(
          state.activeClientIdByMonth,
          action.monthKey,
          action.clientId || ""
        ),
      };

    case types.INVOICES_CLIENT_DETAIL_SUCCESS:
      return {
        ...state,
        loading: false,
        clientInvoiceById: setNested(
          state.clientInvoiceById,
          action.clientId,
          action.record || null
        ),
        tabNewByClient: setNested(
          state.tabNewByClient,
          action.clientId,
          action.tabNew || {}
        ),
        dirtyTabsByClient: setNested(
          state.dirtyTabsByClient,
          action.clientId,
          action.dirtyTabs || {}
        ),
      };

    case types.INVOICES_SET_ACTIVE_TAB:
      return {
        ...state,
        activeTabByClient: setNested(
          state.activeTabByClient,
          action.clientId,
          action.tabId || "labor"
        ),
      };

    case types.INVOICES_SET_TAB_NEW: {
      const clientNew = state.tabNewByClient[action.clientId] || {};
      const currentTabPayload = clientNew?.[action.tabId] || {};
      const nextTabPayload = action.payload || {};

      if (isEqual(currentTabPayload, nextTabPayload)) {
        return state;
      }

      return {
        ...state,
        tabNewByClient: setNested(state.tabNewByClient, action.clientId, {
          ...clientNew,
          [action.tabId]: nextTabPayload,
        }),
      };
    }

    case types.INVOICES_SET_TAB_DIRTY: {
      const clientDirty = state.dirtyTabsByClient[action.clientId] || {};
      const nextDirty = Boolean(action.isDirty);

      if (Boolean(clientDirty?.[action.tabId]) === nextDirty) {
        return state;
      }

      return {
        ...state,
        dirtyTabsByClient: setNested(state.dirtyTabsByClient, action.clientId, {
          ...clientDirty,
          [action.tabId]: nextDirty,
        }),
      };
    }

    case types.INVOICES_SET_TAB_SAVING: {
      const clientSaving = state.tabSavingByClient[action.clientId] || {};
      const nextSaving = Boolean(action.saving);

      if (Boolean(clientSaving?.[action.tabId]) === nextSaving) {
        return state;
      }

      return {
        ...state,
        tabSavingByClient: setNested(state.tabSavingByClient, action.clientId, {
          ...clientSaving,
          [action.tabId]: nextSaving,
        }),
      };
    }

    case types.INVOICES_TAB_SAVE_SUCCESS: {
      const clientDirty = state.dirtyTabsByClient[action.clientId] || {};
      const clientSaving = state.tabSavingByClient[action.clientId] || {};
      const currentClientRecord = state.clientInvoiceById?.[action.clientId] || null;
      const nextClientRecord = action.record || currentClientRecord;
      const nextClientNew = {
        ...(state.tabNewByClient[action.clientId] || {}),
        [action.tabId]: action.payload || {},
        ...(action.invoicePayload
          ? { invoice: action.invoicePayload }
          : {}),
      };

      return {
        ...state,
        clientInvoiceById:
          nextClientRecord !== currentClientRecord
            ? setNested(state.clientInvoiceById, action.clientId, nextClientRecord)
            : state.clientInvoiceById,
        tabNewByClient: setNested(state.tabNewByClient, action.clientId, nextClientNew),
        dirtyTabsByClient: setNested(state.dirtyTabsByClient, action.clientId, {
          ...clientDirty,
          [action.tabId]: false,
        }),
        tabSavingByClient: setNested(state.tabSavingByClient, action.clientId, {
          ...clientSaving,
          [action.tabId]: false,
        }),
      };
    }

    case types.INVOICES_VALIDATION_ERROR: {
      const key = `${action.clientId}:${action.tabId}`;
      const nextErrors = action.errors || {};

      if (isEqual(state.validationErrorsByClientTab?.[key] || {}, nextErrors)) {
        return state;
      }

      return {
        ...state,
        loading: false,
        validationErrorsByClientTab: setNested(
          state.validationErrorsByClientTab,
          key,
          nextErrors
        ),
      };
    }

    case types.INVOICES_CLEAR_VALIDATION_ERROR: {
      const key = `${action.clientId}:${action.tabId}`;
      if (!Object.prototype.hasOwnProperty.call(state.validationErrorsByClientTab || {}, key)) {
        return state;
      }

      const next = { ...(state.validationErrorsByClientTab || {}) };
      delete next[key];
      return {
        ...state,
        validationErrorsByClientTab: next,
      };
    }

    case types.INVOICES_SYNC_CONFIG_RATES:
      return {
        ...state,
        sync: {
          status: "synced",
          message: action.message || "",
          updatedAt: Date.now(),
        },
      };

    case types.INVOICES_SYNC_STATUS:
      return {
        ...state,
        sync: {
          status: action.status || "new",
          message: action.message || "",
          updatedAt: action.updatedAt || state.sync.updatedAt,
        },
      };

    case types.INVOICES_CLIENT_STATUS_UPDATE: {
      const clientId = String(action.clientId || "");
      const monthKey = String(action.monthKey || "");
      if (!clientId) return state;

      const existingDetail = state.clientInvoiceById?.[clientId];
      const hasExistingDetail =
        existingDetail && typeof existingDetail === "object" && !Array.isArray(existingDetail);
      const nextDetail = hasExistingDetail
        ? {
            ...existingDetail,
            ...(action.publishStatus !== undefined
              ? {
                  status: action.publishStatus,
                  invoice_status: action.publishStatus,
                  publish_status: action.publishStatus,
                }
              : {}),
            ...(Object.prototype.hasOwnProperty.call(action, "programStatus")
              ? { program_status: action.programStatus }
              : {}),
          }
        : existingDetail;

      const monthClients = Array.isArray(state.monthClientsByKey?.[monthKey])
        ? state.monthClientsByKey[monthKey]
        : [];
      const nextMonthClients =
        monthKey && monthClients.length > 0
          ? monthClients.map((client) => {
              const resolvedClientId = String(
                client?.program_id || client?.programId || client?.id || ""
              );
              if (resolvedClientId !== clientId) return client;

              return {
                ...client,
                ...(action.publishStatus !== undefined
                  ? {
                      status: action.publishStatus,
                      invoice_status: action.publishStatus,
                      publish_status: action.publishStatus,
                    }
                  : {}),
                ...(Object.prototype.hasOwnProperty.call(action, "programStatus")
                  ? { program_status: action.programStatus }
                  : {}),
              };
            })
          : monthClients;

      return {
        ...state,
        ...(hasExistingDetail
          ? {
              clientInvoiceById: setNested(state.clientInvoiceById, clientId, nextDetail),
            }
          : {}),
        ...(monthKey && monthClients.length > 0
          ? {
              monthClientsByKey: setNested(
                state.monthClientsByKey,
                monthKey,
                nextMonthClients
              ),
            }
          : {}),
      };
    }

    case types.INVOICES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error || "Unable to process invoices request",
      };

    default:
      return state;
  }
};

export default InvoicesReducer;
